/**
 * Constrói o HTML do corpo (sem wrapper de sheet externo) para documentos
 * de texto livre gerados na evolução clínica:
 *   exam_order · refraction · report · surgical_risk · insurance_guide · indication
 *
 * Este HTML é passado como `bodyHtml` de um item de `buildClinicMultiSheetPrintHtml`
 * — o wrapper de sheet com o timbrado/rodapé é adicionado externamente.
 */
import { escapeHtml, CLINIC_DOC_SIGNATURE_CSS, buildClinicalPrintPatientInfoHtml, type ClinicalPrintPatient } from './clinicPrintLetterhead';
import { sanitizeClinicalHtml } from './clinicalHtml';
import { extractRefractionFromExamOrderBody, parseRefractionBody } from '../components/evolution/ophthalmologyRefractionConstants';

export interface EvoTextDocProfessional {
  full_name?: string;
  specialty?: string;
  /** council_type ou registration_type (ambos suportados) */
  council_type?: string;
  registration_type?: string;
  /** council_number ou registration_number (ambos suportados) */
  council_number?: string;
  registration_number?: string;
  /** council_state ou registration_state (ambos suportados) */
  council_state?: string;
  registration_state?: string;
  signature_image_url?: string;
  professional_credentials?: string[];
}

export type EvoTextDocPatient = ClinicalPrintPatient;

export interface EvoTextDocBodyOpts {
  category: string;
  templateName: string;
  bodyText: string;
  patient?: EvoTextDocPatient | null;
  professional?: EvoTextDocProfessional | null;
  /** Data de emissão — cabeçalho e rodapé usam o mesmo valor (como no receituário). */
  issueDate?: string | Date | null;
  /**
   * Título principal do documento (ex.: apelido do relatório médico).
   * Quando informado, não usa o rótulo fixo da categoria (ex. "LAUDO").
   */
  documentTitle?: string;
  /** Evita repetir o nome do modelo abaixo do título principal. */
  omitTemplateName?: boolean;
  /** Sem título central nem linha "Emissão:" acima do paciente (ex.: relatório médico). */
  omitDocumentHeading?: boolean;
}

const TITLE_MAP: Record<string, string> = {
  exam_order: 'SOLICITAÇÃO DE EXAMES',
  refraction: 'PRESCRIÇÃO DE REFRAÇÃO',
  report: 'LAUDO',
  surgical_risk: 'RISCO CIRÚRGICO',
  insurance_guide: 'GUIA DE CONVÊNIO',
  indication: 'INDICAÇÕES',
};

function formatIssueDate(d?: string | Date | null): string {
  if (!d) return new Date().toLocaleDateString('pt-BR');
  try {
    const dt = d instanceof Date ? d : new Date(String(d).split('T')[0] + 'T12:00:00');
    return dt.toLocaleDateString('pt-BR');
  } catch {
    return new Date().toLocaleDateString('pt-BR');
  }
}

function refractionCell(value: string): string {
  const v = escapeHtml(value.trim());
  return `<td class="etd-ref-cell">${v || '&nbsp;'}</td>`;
}

export function buildRefractionTableHtml(bodyText: string): string {
  const v = parseRefractionBody(bodyText);
  const notes = escapeHtml(v.notes).replace(/\n/g, '<br/>');
  return `<style>
  .etd-ref-table { width:100%; border-collapse:collapse; margin:0 0 14px; }
  .etd-ref-table th, .etd-ref-table td { border:1px solid #94a3b8; padding:8px 10px; font-size:12px; background:#fff; }
  .etd-ref-table th { color:#0f172a; font-weight:700; text-align:center; }
  .etd-ref-table td.etd-ref-label { font-weight:700; color:#334155; width:28%; }
  .etd-ref-cell { text-align:center; font-weight:700; min-width:4.5em; }
  .etd-ref-near { margin:0 0 14px; }
  .etd-ref-near p { margin:0 0 8px; font-size:12px; color:#0f172a; }
  .etd-ref-near-box { display:inline-block; min-width:6em; background:#fff; border:1px solid #94a3b8; padding:8px 16px; text-align:center; font-weight:700; font-size:13px; }
  .etd-ref-notes { font-size:12px; line-height:1.55; color:#1f2937; white-space:pre-wrap; }
</style>
<p style="margin:0 0 8px;font-weight:700;">Para longe.</p>
<table class="etd-ref-table">
  <thead>
    <tr>
      <th></th>
      <th>Esférico</th>
      <th>Cilíndrico</th>
      <th>Eixo</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td class="etd-ref-label">Olho Direito</td>
      ${refractionCell(v.odSph)}
      ${refractionCell(v.odCyl)}
      ${refractionCell(v.odAxis)}
    </tr>
    <tr>
      <td class="etd-ref-label">Olho Esquerdo</td>
      ${refractionCell(v.osSph)}
      ${refractionCell(v.osCyl)}
      ${refractionCell(v.osAxis)}
    </tr>
  </tbody>
</table>
<div class="etd-ref-near">
  <p>Para perto: adicionar em refração de ambos os olhos no valor esférico de:</p>
  <div class="etd-ref-near-box">${escapeHtml(v.nearAdd.trim()) || '&nbsp;'}</div>
</div>
<p style="margin:0 0 6px;font-weight:700;">Observações:</p>
<div class="etd-ref-notes">${notes || '—'}</div>`;
}

function buildOphthalmologyExamOrderBodyHtml(bodyText: string): string {
  const { bodyWithoutMarker, refraction } = extractRefractionFromExamOrderBody(bodyText);
  const esc = escapeHtml;
  const lines = bodyWithoutMarker.split('\n');
  const htmlLines: string[] = [];
  let inObs = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      htmlLines.push('<br/>');
      continue;
    }
    if (/^Observações:?$/i.test(trimmed)) {
      inObs = true;
      htmlLines.push(`<p style="margin:12px 0 6px;font-weight:700;">${esc(trimmed)}</p>`);
      continue;
    }
    if (/^Legenda:/i.test(trimmed)) {
      inObs = false;
      htmlLines.push(`<p style="margin:14px 0 0;font-size:11px;color:#64748b;">${esc(trimmed)}</p>`);
      continue;
    }
    if (inObs && /^Refração \(vide grade abaixo\)\.?$/i.test(trimmed)) {
      continue;
    }
    htmlLines.push(`<p style="margin:0 0 4px;${inObs ? '' : 'font-size:13px;'}">${esc(trimmed)}</p>`);
  }

  const refractionBlock =
    refraction && (refraction.odSph || refraction.odCyl || refraction.odAxis || refraction.osSph || refraction.osCyl || refraction.osAxis || refraction.nearAdd)
      ? `<div style="margin-top:10px;">${buildRefractionTableHtml(JSON.stringify(refraction))}</div>`
      : '';

  return `${htmlLines.join('\n')}${refractionBlock}`;
}

export function buildEvolutionTextDocBodyHtml(opts: EvoTextDocBodyOpts): string {
  const {
    category,
    templateName,
    bodyText,
    patient,
    professional,
    issueDate,
    documentTitle,
    omitTemplateName,
    omitDocumentHeading,
  } = opts;
  const esc = escapeHtml;

  const titleKey = documentTitle ? undefined : TITLE_MAP[category];
  const docTitle = documentTitle || titleKey || templateName.toUpperCase();
  const titleNaturalCase = Boolean(documentTitle);
  const showTemplateName = !omitTemplateName
    && Boolean(titleKey && templateName && templateName.toUpperCase() !== docTitle);
  const emissionDateStr = formatIssueDate(issueDate);

  const isHtml = /<[a-zA-Z]/.test(bodyText);
  const bodyRendered =
    category === 'refraction'
      ? buildRefractionTableHtml(bodyText)
      : category === 'exam_order' && bodyText.includes('SOLICITAÇÃO DE EXAMES — OFTALMOLOGIA')
        ? buildOphthalmologyExamOrderBodyHtml(bodyText)
        : isHtml
          ? sanitizeClinicalHtml(bodyText)
          : esc(bodyText).replace(/\n/g, '<br/>');

  const patientBlock = patient?.full_name
    ? buildClinicalPrintPatientInfoHtml(patient, {
        variant: category === 'indication' ? 'detailed' : 'compact',
        issueDate: issueDate ?? undefined,
      })
    : '';

  const sigImgUrl = String(professional?.signature_image_url ?? '').trim();
  const sigImg = sigImgUrl
    ? `<div class="clinic-doc-sign-prof-img-wrap"><img src="${esc(sigImgUrl)}" alt="" /></div>`
    : '';
  const spacerCls = sigImg ? 'clinic-doc-sign-spacer clinic-doc-sign-spacer--with-img' : 'clinic-doc-sign-spacer';

  const credHtml = Array.isArray(professional?.professional_credentials)
    ? professional!.professional_credentials
        .filter(Boolean)
        .map((c) => `<p class="clinic-doc-sign-caption" style="font-style:italic;color:#374151;">${esc(c)}</p>`)
        .join('')
    : '';

  const regNumber = professional?.council_number || professional?.registration_number;
  const regType = professional?.council_type || professional?.registration_type || 'CRM';
  const regState = professional?.council_state || professional?.registration_state;
  const regLine = regNumber
    ? `<p class="clinic-doc-sign-caption">${esc(regType)} ${esc(regNumber)}${regState ? ` - ${esc(regState)}` : ''}</p>`
    : '';

  const signatureBlock = professional?.full_name
    ? `<div class="clinic-doc-sign-zone">
  <div class="clinic-doc-sign-row">
    <div class="clinic-doc-sign-slot">
      ${sigImg}
      <div class="${spacerCls}"></div>
      <div class="clinic-doc-sign-rule"></div>
      <p class="clinic-doc-sign-caption">${esc(professional.full_name)}</p>
      ${professional.specialty ? `<p class="clinic-doc-sign-caption">${esc(professional.specialty)}</p>` : ''}
      ${credHtml}
      ${regLine}
    </div>
    <div class="clinic-doc-sign-slot clinic-doc-sign-slot--narrow">
      <div class="clinic-doc-sign-spacer"></div>
      <div class="clinic-doc-sign-rule"></div>
      <p class="clinic-doc-sign-caption">Data: ${emissionDateStr}</p>
    </div>
  </div>
</div>`
    : '';

  const headingBlock = omitDocumentHeading
    ? ''
    : `<div class="etd-title${titleNaturalCase ? ' etd-title--natural' : ''}">${esc(docTitle)}</div>
<div class="etd-date">Emissão: ${emissionDateStr}</div>`;

  return `<style>
  .etd-title { text-align:center; font-size:15px; font-weight:700; color:#2563eb; margin:4px 0 6px; text-transform:uppercase; letter-spacing:1px; }
  .etd-title--natural { text-transform:none; letter-spacing:0.02em; }
  .etd-date { text-align:center; font-size:10px; color:#9ca3af; margin-bottom:14px; }
  ${CLINIC_DOC_SIGNATURE_CSS}
</style>
${headingBlock}
${patientBlock}
<div class="section">
  ${showTemplateName ? `<div class="section-title">${esc(templateName)}</div>` : ''}
  <div class="attachment-body">${bodyRendered}</div>
</div>
${signatureBlock}`;
}
